import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  TableContainer,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Modal,
  Box,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import axios from "axios";
import { putData, Url } from "../../../Component/function/FunctionApi";
import { getCookie } from "../../../Pages/Login/Login";
import usePrintInvoices from "../../../hooks/UsePrint";
import usePrintInvoicesSingle from "../../../hooks/UsePrintInvoices";
import useExportExcel from "../../../hooks/ExportExcel/userExportExcel";
import Swal from "sweetalert2";
import useShowInvoice from "../../../hooks/invoice/ShowInvoice";
import RepresentativeSelector from "../../../Component/RepresentativeSelector/RepresentativeSelector";
import DelayInvoiceDialog from "../../../Component/DelayInvoiceDialog/DelayInvoiceDialog";
import SearchControls from "../components/SearchControls";
import SearchInputsRow from "../components/SearchInputsRow";
import TableHeader from "../components/TableHeader";
import StatusSummaryCard from "../components/StatusSummaryCard";
const authHeaders = () => ({
  Authorization: `Bearer ${
    getCookie("user") ? JSON.parse(getCookie("user")).token : ""
  }`,
});
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};


const DataTable = () => {
  const printRepInvoices = usePrintInvoices();
  const printRepInvoicesSingle = usePrintInvoicesSingle();
  const { handleExportToExcel } = useExportExcel();
  const invoiceCodeRef = useRef();
  const customerRef = useRef();
  const addressRef = useRef();
  const governorateRef = useRef();
  const phoneRef = useRef();
  const invoiceCategoriesRef = useRef();
  const dateCreatedRef = useRef();
  const userNameRef = useRef();
  const statusRef = useRef();
  const totalPriceRef = useRef();

  const [apiResult, setApiResult] = useState({
    data: [],
    totalCount: 0,
    totalPages: 0,
    currentPage: 1,
    pageSize: 10,
    hasPreviousPage: false,
    hasNextPage: false,
  });
  const [searchTerms, setSearchTerms] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const debouncedSearchTerms = useDebounce(searchTerms, 1000);
  const [statusMessage, setStatusMessage] = useState(null);
  const [openRepModal, setOpenRepModal] = useState(false);
  const [representatives, setRepresentatives] = useState([]);
  const [selectedRepresentative, setSelectedRepresentative] = useState("");
  const dateFromRef = useRef();
  const dateToRef = useRef();


  const [showPrintModel, setShowPrintModel] = useState(false);
  const [showDelayModal, setShowDelayModal] = useState(false);
  const [isDelayingInvoices, setIsDelayingInvoices] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  });
  const [statusMessageModalOpen, setStatusMessageModalOpen] = useState(false);

  const buildParams = (pageNumber, pageSize) => {
    const params = {
      PageNumber: pageNumber || apiResult.currentPage || 1,
      PageSize: pageSize || apiResult.pageSize || 10,
    };

    const term = (key) => debouncedSearchTerms[key]?.toString().trim();

    const invoiceCode = term("invoiceCode");
    if (invoiceCode) {
      const parts = invoiceCode
        .split("-")
        .map((p) => p.trim())
        .filter(Boolean);
      if (parts.length > 1) {
        params.Codes = parts.join(",");
      } else {
        params.Code = parts[0];
      }
    }
    if (term("invoiceDate")) params.Date = term("invoiceDate");
    if (term("customer")) params.ClientName = term("customer");
    if (term("createdBy")) params.SecretaryName = term("createdBy");
    if (term("address")) params.Address = term("address");
    if (term("governorate")) params.Governorate = term("governorate");
    if (term("phone")) params.Phone = term("phone");
    if (term("invoiceCategories"))
      params.CategoryName = term("invoiceCategories");
    if (term("dailyDate")) params.RepresentativeAssignDate = term("dailyDate");
    if (term("fullName")) params.RepresentativeName = term("fullName");
    if (term("status")) params.Status = term("status");
    if (term("totalPrice")) params.TotalPrice = Number(term("totalPrice"));
    if (term("dateFrom")) params.DateFrom = term("dateFrom");
    if (term("dateTo")) params.DateTo = term("dateTo");

    return params;
  };

  const fetchInvoices = async (
    pageNumber = 1,
    pageSize = apiResult.pageSize || 10
  ) => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${Url}Invoice/get-invoice-pagination`, {
        headers: authHeaders(),
        params: buildParams(pageNumber, pageSize),
      });
      const payload = response.data || {};
      setApiResult({
        data: payload.data || [],
        totalCount: payload.totalCount || 0,
        totalPages: payload.totalPages || 0,
        currentPage: payload.currentPage || pageNumber,
        pageSize: payload.pageSize || pageSize,
        hasPreviousPage: Boolean(payload.hasPreviousPage),
        hasNextPage: Boolean(payload.hasNextPage),
      });
      setSelectedRows([]);
    } catch (e) {
      setError("Error fetching data from server");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Initial load: no filters
    fetchInvoices(1, apiResult.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refetch when search terms change (debounced)
  useEffect(() => {
    fetchInvoices(1, apiResult.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerms]);

  const { showInvoice } = useShowInvoice();

  const handleSearch = (column, value) => {
    setSearchTerms((prev) => ({ ...prev, [column]: value }));
  };
 


    const handleUpdateRepresentative = async () => {
      try {
        const requestBody = {
          invoiceIds: selectedRows,
          newRepresentativeId: (selectedRepresentative === "null" || selectedRepresentative === "") ? null : selectedRepresentative,
          dateDaily: new Date(selectedDate).toISOString(),
        };
        console.log(requestBody);
        await putData("Invoice/update-representative-invoices", requestBody);
        setStatusMessage("تم تعديل المندوب بنجاح");
        window.location.reload();
      } catch (error) {
        console.error("Error updating representative:", error);
        setStatusMessage(error.response.data.message);
      } finally {
        setStatusMessageModalOpen(true);
        setOpenRepModal(false);
      }
    };

  const handleDelayInvoice = async (postponedUntil) => {
    setIsDelayingInvoices(true);
    
    try {
      // Process each selected invoice
      const delayPromises = selectedRows.map(async (invoiceId) => {
        const requestBody = {
          isPostponed: true,
          postponedUntil: new Date(postponedUntil).toISOString(),
        };
        
        return await putData(`Invoice/postpone/${invoiceId}`, requestBody);
      });

      await Promise.all(delayPromises);
      
      setStatusMessage("تم تأجيل الفواتير بنجاح");
      setShowDelayModal(false);
      setSelectedRows([]);
      await fetchInvoices(apiResult.currentPage, apiResult.pageSize);
      setStatusMessageModalOpen(true);
    } catch (error) {
      console.error("Error delaying invoices:", error);
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: error.response?.data?.message || "حدث خطأ أثناء تأجيل الفواتير",
      });
    } finally {
      setIsDelayingInvoices(false);
    }
  };

  const handleOpenDelayModal = () => {
    setShowDelayModal(true);
  };

  const handleCloseDelayModal = () => {
    if (!isDelayingInvoices) {
      setShowDelayModal(false);
    }
  };
  
  const handleSelectAll = (event) => {
    setSelectedRows(
      event.target.checked ? (apiResult.data || []).map((row) => row.id) : []
    );
  };

  const handleOpenRepModal = () => {
    setOpenRepModal(true);
  };
  const handleRowSelect = (event, row) => {
    setSelectedRows((prev) =>
      event.target.checked
        ? [...prev, row.id]
        : prev.filter((id) => id !== row.id)
    );
  };

  const handlePrint = () => {
    if (selectedRows.length === 0) {
      alert("لا يوجد بيانات للطباعة");
      return;
    }

    const data = (apiResult.data || []).filter((row) =>
      selectedRows.includes(row.id)
    );

    printRepInvoicesSingle(data);
  };

  const handleOpenModal = () => {
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedStatus("");
    setStatusMessage(null);
  };

  const handleStatusChange = (event) => {
    setSelectedStatus(event.target.value);
  };

  const handleUpdateStatus = async () => {
    try {
      await axios.put(
        `${Url}Invoice/update-invoice-status`,
        {
          InvoiceIds: selectedRows,
          InvoiceStatus: selectedStatus,
        },
        { headers: { ...authHeaders(), "Content-Type": "application/json" } }
      );
      setStatusMessage("تم تعديل حالة الفاتورة بنجاح");
      await fetchInvoices(apiResult.currentPage, apiResult.pageSize);
    } catch (error) {
      Swal.fire("Error", error?.response?.data?.message || "", "error");
    } finally {
      setStatusMessageModalOpen(true);
      handleCloseModal();
    }
  };

  const handleClearSearch = () => {
    invoiceCodeRef.current.value = "";
    dateFromRef.current.value = "";
    dateToRef.current.value = "";
    customerRef.current.value = "";
    addressRef.current.value = "";
    governorateRef.current.value = "";
    phoneRef.current.value = "";
    invoiceCategoriesRef.current.value = "";
    dateCreatedRef.current.value = "";
    userNameRef.current.value = "";
    statusRef.current.value = "";
    totalPriceRef.current.value = "";

    setSearchTerms({});
    fetchInvoices(1, apiResult.pageSize);
  };

  useEffect(() => {
    const loadReps = async () => {
      try {
        const reps = await axios.get(`${Url}Represntative/get-Represntatives`, {
          headers: authHeaders(),
        });
        setRepresentatives(reps.data || []);
      } catch {}
    };
    loadReps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // if (loading) return <CircularProgress />;
  // if (error) return <div>{error}</div>;

  const handelPrintDaily = async () => { 
     console.log('print')
    try {
      const url = `Represntative/get-dailyDateOfRepresntative?represntativeId=${selectedRepresentative}&dateDaily=${selectedDate}`;
      const response = await axios.get(`${Url}${url}`, {
        headers: authHeaders(),
      });
      const data = await response.data.data; 
       console.log(data)
      if (!data?.[0]?.invoices?.length) {
        setShowPrintModel(false);
        Swal.fire({
          icon: "info",
          title: "لا يوجد  فواتير",
          text: "لا يوجد فواتير لهذا المندوب  في هذه اليوم",
        });
      } else {
        printRepInvoices(data);
      }
    } catch (error) {
      Swal.fire({
        icon: "خطا",
        title: "Error",
        text: "حدث خطاء في جلب البيانات اليومية",
      });
    }
  };

 const renderSearchInput = (column, placeholder, ref) => (
  <td key={column}>
    <input
      type={
        column === "dailyDate" ||
        column === "dateFrom" ||
        column === "dateTo"
          ? "date"
          : "text"
      }
      placeholder={placeholder}
      value={searchTerms[column] || ""}
      onChange={(e) => handleSearch(column, e.target.value)}
      ref={ref}
      className="w-full p-1 border   placeholder::text-xs focus:outline-none focus:border-blue-600 border-gray-300 rounded-md"
    />
  </td>
);

  const currentRows = apiResult.data || [];

// delivered AND returned Items

const deliveredItems = useMemo(() => {
  return (apiResult.data || [])
    .filter(inv => inv.status === "تم_التسليم")
    .flatMap(inv =>
      (inv.invoiceCategories || []).map(cat => ({
        name: cat.category?.name || "غير معروف",
        qty: cat.quantity || 0,
        price: cat.total || 0,
      }))
    );
}, [apiResult.data]);

const returnedItems = useMemo(() => {
  return (apiResult.data || [])
    .filter(inv => inv.status === "مرتجع")
    .flatMap(inv =>
      (inv.invoiceCategories || []).map(cat => ({
        name: cat.category?.name || "غير معروف",
        qty: cat.quantity || 0,
        price: cat.total || 0,
      }))
    );
}, [apiResult.data]);

const groupItems = (items) => {
  const map = {};
  items.forEach(i => {
    if (!map[i.name]) {
      map[i.name] = { ...i };
    } else {
      map[i.name].qty += i.qty;
      map[i.name].price += i.price;
    }
  });
  return Object.values(map);
}
  //=============== statusSummary ============= 

const statusSummary = useMemo(() => ({
  delivered: {
    qty: deliveredItems.reduce((s, i) => s + i.qty, 0),
    total: deliveredItems.reduce((s, i) => s + i.price, 0),
  },
  returned: {
    qty: returnedItems.reduce((s, i) => s + i.qty, 0),
    total: returnedItems.reduce((s, i) => s + i.price, 0),
  },
}), [deliveredItems, returnedItems]);

// =================================


  return (
    <div>
      <Paper>
        <SearchControls
          totalFiltered={currentRows.length}
          selectedCount={selectedRows.length}
          onExport={() => handleExportToExcel(currentRows, selectedRows,statusSummary)}
          onClear={handleClearSearch}
          onPrint={handlePrint}
        />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 mt-4">
          <StatusSummaryCard
            title="✔ تم التسليم"
            color="border-green-500"
            items={groupItems(deliveredItems)}
          />

          <StatusSummaryCard
            title="↩ مرتجع"
            color="border-red-500"
            items={groupItems(returnedItems)}
          />
        </div>

        <TableContainer>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {/* Search Inputs Row */}
                <SearchInputsRow
                  renderSearchInput={renderSearchInput}
                  refs={{
                    invoiceCodeRef,
                    dateFromRef,
                    dateToRef,
                    customerRef,
                    addressRef,
                    governorateRef,
                    phoneRef,
                    invoiceCategoriesRef,
                    dateCreatedRef,
                    userNameRef,
                    statusRef,
                    totalPriceRef,
                  }}
                />

                {/* Header Row */}
                <TableHeader
                  onSelectAll={handleSelectAll}
                  allSelected={
                    selectedRows.length === currentRows.length &&
                    currentRows.length > 0
                  }
                />
              </thead>

              {loading ? (
                <div className="flex justify-center items-center ">
                  <CircularProgress />
                </div>
              ) : (
                <tbody className="bg-white divide-y divide-gray-200">
                  {currentRows.length > 0 ? (
                    currentRows.map((row) => (
                      <tr
                        key={row.id}
                        className={
                          selectedRows.includes(row.id)
                            ? "bg-blue-50"
                            : "hover:bg-gray-50"
                        }
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <input
                            type="checkbox"
                            className="form-checkbox"
                            checked={selectedRows.includes(row.id)}
                            onChange={(event) => handleRowSelect(event, row)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-bold">
                          {row.invoiceCode}
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          {row.invoiceDate.split("T")[0]}
                        </td>
                        <td className="p-1 break-words whitespace-wrap">
                           {row.invoiceDateTo?.split("T")[0] || "-"}
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          {row.customer}
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          {row.createdBy || ""}
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          {row.address}
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          {row.governorate?.name}
                        </td>
                        <td className="p-1 flex flex-col gap-1  ">
                          <p> {row.phone}</p> <p> {row?.socendPhone || null}</p>
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          {row.invoiceCategories
                            ?.map((cat) => cat.category.name)
                            .join(", ")}
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          {row?.dailyDate?.split("T")[0] || "لا يوجد تاريخ"}
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          {row.representative?.fullName ?? "لا يوجد مندوب"}
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          {row.status}
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          {row.totalPrice}
                        </td>
                        <td className="p-1  break-words whitespace-wrap">
                          <button
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                            onClick={() => showInvoice(row)}
                          >
                            عرض
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={14}
                        className="px-6 py-4 text-center text-gray-500"
                      >
                        No data available
                      </td>
                    </tr>
                  )}
                </tbody>
              )}
            </table>
          </div>
        </TableContainer>
      </Paper>

      {/* Pagination */}
      <div className="flex flex-col items-center justify-center gap-4 py-6">
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() =>
              apiResult.currentPage > 1 &&
              fetchInvoices(apiResult.currentPage - 1, apiResult.pageSize)
            }
            disabled={apiResult.currentPage === 1}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2
              ${
                apiResult.currentPage === 1
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 hover:border-gray-400"
              }`}
          >
            السابق
          </button>

          <div className="flex items-center gap-1">
            {Array.from(
              { length: Math.max(1, apiResult.totalPages) },
              (_, index) => {
                const pageNumber = index + 1;
                const isNearCurrentPage =
                  Math.abs(pageNumber - apiResult.currentPage) <= 1;
                const isFirstPage = pageNumber === 1;
                const isLastPage = pageNumber === apiResult.totalPages;

                if (isFirstPage || isLastPage || isNearCurrentPage) {
                  return (
                    <button
                      key={pageNumber}
                      onClick={() =>
                        fetchInvoices(pageNumber, apiResult.pageSize)
                      }
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200
                      ${
                        apiResult.currentPage === pageNumber
                          ? "bg-blue-600 text-white hover:bg-blue-700"
                          : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 hover:border-gray-400"
                      }`}
                    >
                      {pageNumber}
                    </button>
                  );
                } else if (
                  (pageNumber === 2 && apiResult.currentPage > 3) ||
                  (pageNumber === apiResult.totalPages - 1 &&
                    apiResult.currentPage < apiResult.totalPages - 2)
                ) {
                  return (
                    <span key={pageNumber} className="px-2">
                      ...
                    </span>
                  );
                }
                return null;
              }
            )}
          </div>

          <button
            onClick={() =>
              apiResult.currentPage < apiResult.totalPages &&
              fetchInvoices(apiResult.currentPage + 1, apiResult.pageSize)
            }
            disabled={apiResult.currentPage === apiResult.totalPages}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2
              ${
                apiResult.currentPage === apiResult.totalPages
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-300 hover:border-gray-400"
              }`}
          >
            التالي
          </button>
        </div>

        <div className="text-sm text-gray-600">
          صفحة {apiResult.currentPage} من {Math.max(1, apiResult.totalPages)}
        </div>
      </div>

      <div className="div-open-modal">
        <div className="flex justify-center items-center">
          <div className="all-button">
            {selectedRows.length > 0 && (
              <div>
                <button className="open-modal-button" onClick={handleOpenModal}>
                  تعديل حالة الفاتورة
                </button>
              </div>
            )}
            <div>
              <button
                className="open-modal-button"
                onClick={() => setShowPrintModel(true)}
              >
                طباعه يوميه مندوب
              </button>
            </div>
            <div>
              {selectedRows.length > 0 && (
                <button
                  className="open-modal-button"
                  onClick={handleOpenRepModal}
                >
                  تعديل المندوب
                </button>
              )}
            </div>
            <div>
              {selectedRows.length > 0 && (
                <button
                  className="open-modal-button"
                  onClick={handleOpenDelayModal}
                >
                  تأجيل الفواتير
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <Modal open={openModal} onClose={handleCloseModal} dir="rtl">
        <Box sx={{ padding: 4, backgroundColor: "white", borderRadius: 2 }}>
          <Typography variant="h6">تحديث حالة الفواتير</Typography>
          <Select
            value={selectedStatus}
            onChange={handleStatusChange}
            displayEmpty
            fullWidth
            variant="outlined"
            sx={{ marginBottom: 2 }}
          >
            <MenuItem value="" disabled>
              اختر الحالة الجديدة
            </MenuItem>
            <MenuItem value={1}>جديد</MenuItem>
            <MenuItem value={2}>تحت التجهيز</MenuItem>
            <MenuItem value={3}>تم التسليم</MenuItem>
            <MenuItem value={4}>مرتجع</MenuItem>
            <MenuItem value={5}>ملغي</MenuItem>
          </Select>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateStatus}
          >
            تأكيد
          </Button>
          <Button
            variant="outlined"
            onClick={handleCloseModal}
            style={{ marginRight: 8 }}
          >
            إلغاء
          </Button>
        </Box>
      </Modal>
      {openRepModal && (
        <Modal
          open={openRepModal}
          onClose={() => setOpenRepModal(false)}
          dir="rtl"
        >

          <Box sx={{ padding: 4, backgroundColor: "white", borderRadius: 2 }}>
            <Typography variant="h6">تحديث المندوب</Typography>
            <RepresentativeSelector
              value={selectedRepresentative}
              onChange={(e) => setSelectedRepresentative(e.target.value)}
              label="اختر المندوب"
              sx={{ marginBottom: 2 }}
            />
            <TextField
              variant="outlined"
              label="التاريخ"
              type="date"
              fullWidth
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ marginBottom: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpdateRepresentative}
            >
              تأكيد
            </Button>
            <Button
              variant="outlined"
              onClick={() => setOpenRepModal(false)}
              style={{ marginRight: 8 }}
            >
              إلغاء
            </Button>
          </Box>
        </Modal>
      )}
      {showPrintModel && (
        <Modal
          open={showPrintModel}
          onClose={() => setOpenRepModal(false)}
          dir="rtl"
        >
          <Box sx={{ padding: 4, backgroundColor: "white", borderRadius: 2 }}>
            <Typography variant="h6">طباعه يوميه المندوب</Typography>
            <RepresentativeSelector
              value={selectedRepresentative}
              onChange={(e) => setSelectedRepresentative(e.target.value)}
              label="اختر المندوب"
              sx={{ marginBottom: 2 }}
            />
            <TextField
              variant="outlined"
              label="التاريخ"
              type="date"
              fullWidth
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ marginBottom: 2 }}
            />
            <Button
              variant="contained"
              color="primary"
              onClick={handelPrintDaily}
            >
              طباعه
            </Button>
            <Button
              variant="outlined"
              onClick={() => setShowPrintModel(false)}
              style={{ marginRight: 8 }}
            >
              إلغاء
            </Button>
          </Box>
        </Modal>
      )}
      <Modal
        open={statusMessageModalOpen}
        onClose={() => {
          setStatusMessageModalOpen(false);
        }}
        dir="rtl"
      >
        <Box sx={{ padding: 4, backgroundColor: "white", borderRadius: 2 }}>
          <Typography variant="h5" sx={{ padding: 2, borderRadius: 2 }}>
            {statusMessage}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setStatusMessageModalOpen(false)}
          >
            إغلاق
          </Button>
        </Box>
      </Modal>

      {/* Delay Invoice Dialog */}
      <DelayInvoiceDialog
        open={showDelayModal}
        onClose={handleCloseDelayModal}
        selectedInvoicesCount={selectedRows.length}
        onConfirm={handleDelayInvoice}
        isLoading={isDelayingInvoices}
      />
    </div>
  );
};

export default DataTable;
